'use client'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { createContext, useCallback, useLayoutEffect, useState } from 'react'
import type { ResponsiveType } from 'responsive/types/types'
import { getMatches } from 'responsive/utils/getMatches'
import { mediaQueries } from 'responsive/utils/mediaQueries'

type ResponsiveProviderProps = {
	readonly children: React.ReactNode
}

export const ResponsiveContext = createContext<ResponsiveType | null>(null)

export function ResponsiveProvider({ children }: ResponsiveProviderProps) {
	/**
	 * To prevent the flickering of the screen when hydrating the page,
	 * we need to set the initial state to the most probable value.
	 *
	 * Most probably is you have a page request from a small device:
	 * - isTouchable: most probably you are using a touchable screen
	 * - isSmall: you are requesting from a mobile device
	 * - isMedium: you are requesting from a tablet device
	 * - isLessThanLarge: you are requesting from a tablet or mobile
	 * - isLessThanExtraLarge: you are requesting from a tablet or mobile
	 */

	const { isMobile: requestedFromSmallDevice } = useMasterData()
	const [responsiveInfo, setResponsiveInfo] = useState<ResponsiveType>({
		availableScreenSize: false,
		isTouchable: requestedFromSmallDevice,
		isSmall: requestedFromSmallDevice,
		isMedium: requestedFromSmallDevice,
		isLarge: !requestedFromSmallDevice,
		isLessThanLarge: requestedFromSmallDevice,
		isLargeOrGreater: !requestedFromSmallDevice,
		isLessThanExtraLarge: requestedFromSmallDevice,
		isExtraLargeOrGreater: !requestedFromSmallDevice,
	})

	const handleChangeBreakpoint = useCallback(() => {
		setResponsiveInfo(getMatches)
	}, [])

	useLayoutEffect(() => {
		handleChangeBreakpoint()
	}, [handleChangeBreakpoint])

	useLayoutEffect(() => {
		const responsiveMediaQueries = mediaQueries.map((q) => window.matchMedia(q))

		for (const mq of responsiveMediaQueries) {
			mq.addEventListener('change', handleChangeBreakpoint)
		}

		return () => {
			for (const mq of responsiveMediaQueries) {
				mq.removeEventListener('change', handleChangeBreakpoint)
			}
		}
	}, [handleChangeBreakpoint])

	return (
		<ResponsiveContext.Provider value={responsiveInfo}>
			{children}
		</ResponsiveContext.Provider>
	)
}
